import React, { useId } from 'react';
import ProviderKeyValueFields from './ProviderKeyValueFields';
import PaymentGatewayRateTable from '../PaymentGatewayRateTable';
import { normalizeCredentialFields } from '../../utils/credentialFields';
import { mergeProviderChangeConfig } from '../../utils/ticketProviderConfiguration';
export { buildDefaultProviderChangeConfig, buildExistingProviderChangeConfig, mergeProviderChangeConfig } from '../../utils/ticketProviderConfiguration';

const fieldLabelStyle = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.74rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase'
};

const sectionStyle = {
  border: '1px solid var(--border-light)',
  borderRadius: '8px',
  padding: '0.85rem',
  backgroundColor: 'var(--bg-soft)'
};

const normalizeUrlList = (values = []) => (
  Array.isArray(values) && values.length > 0 ? values : ['']
);

const ProviderChangeConfigFields = ({ provider, value, onChange, disabled = false, allowStatusChange = false, allowCustomFields = false }) => {
  const id = useId();
  const config = mergeProviderChangeConfig(provider, value || {});
  const apiCredentialKeys = normalizeCredentialFields(provider?.api_credential_keys);
  const boCredentialKeys = normalizeCredentialFields(provider?.bo_credential_keys);
  const callbackDefs = Array.isArray(provider?.callback_urls) ? provider.callback_urls : [];
  const credentialKeys = [...apiCredentialKeys, ...boCredentialKeys].map(field => field.key);
  const reservedKeys = [...credentialKeys, 'bo_urls', 'api_urls', 'callback_paths'];
  const callbackNames = callbackDefs.map(field => field.name);
  const groupLinks = Array.isArray(config.group_links) && config.group_links.length > 0
    ? config.group_links
    : [{ name: '', url: '' }];

  const updateConfig = (patch) => onChange({ ...config, ...patch });
  const updateCredentials = (env, patch) => {
    updateConfig({
      credentials: {
        ...(config.credentials || {}),
        [env]: {
          ...(config.credentials?.[env] || {}),
          ...patch
        }
      }
    });
  };
  const updateUrl = (env, field, index, nextValue) => {
    const nextUrls = [...normalizeUrlList(config.credentials?.[env]?.[field])];
    nextUrls[index] = nextValue;
    updateCredentials(env, { [field]: nextUrls });
  };
  const removeUrl = (env, field, index) => {
    const nextUrls = normalizeUrlList(config.credentials?.[env]?.[field]).filter((_, itemIndex) => itemIndex !== index);
    updateCredentials(env, { [field]: nextUrls.length > 0 ? nextUrls : [''] });
  };
  const addUrl = (env, field) => {
    updateCredentials(env, { [field]: [...normalizeUrlList(config.credentials?.[env]?.[field]), ''] });
  };
  const updateGroupLink = (index, field, nextValue) => {
    const nextLinks = [...groupLinks];
    nextLinks[index] = { ...nextLinks[index], [field]: nextValue };
    updateConfig({ group_links: nextLinks });
  };

  if (!provider) return null;

  return (
    <div style={{ display: 'grid', gap: '0.85rem' }}>
      {provider.paymentGateway && (config.selling_rate_rules === null || config.selling_rate === null ? <p className="ticket-muted">Selling rates restricted.</p> : <PaymentGatewayRateTable provider={provider}
        rules={config.selling_rate_rules} disabled={disabled} onChange={selling_rate_rules => updateConfig({ selling_rate_rules })} />)}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
        {!provider.paymentGateway && <div>
          <label htmlFor={`${id}-rate`} style={fieldLabelStyle}>Selling Rate (%)</label>
          <input id={`${id}-rate`} className="input" type="number" min="0" max="100" step="0.01" value={config.selling_rate ?? ''} onChange={(event) => updateConfig({ selling_rate: event.target.value })} disabled={disabled || config.selling_rate === null} />
        </div>}
        {allowStatusChange && (
          <div>
            <label htmlFor={`${id}-status`} style={fieldLabelStyle}>Provider Status</label>
            <select id={`${id}-status`} className="input" value={config.status} onChange={(event) => updateConfig({ status: event.target.value })} disabled={disabled}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}
        <label style={{ ...sectionStyle, display: 'flex', alignItems: 'center', gap: '0.6rem', minHeight: '42px', cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <input type="checkbox" checked={!!config.is_client_line} onChange={(event) => updateConfig({ is_client_line: event.target.checked })} disabled={disabled || config.is_client_line === null} />
          <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-secondary)' }}>Client Line</span>
        </label>
      </div>

      <div style={sectionStyle}>
        <label style={fieldLabelStyle}>Group Links</label>
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {groupLinks.map((link, index) => (
            <div key={`group-link-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.4fr) minmax(0, 1fr) auto', gap: '0.5rem' }}>
              <input className="input" value={link.name || ''} onChange={(event) => updateGroupLink(index, 'name', event.target.value)} placeholder="Name" disabled={disabled} />
              <input className="input" value={link.url || ''} onChange={(event) => updateGroupLink(index, 'url', event.target.value)} placeholder="URL" disabled={disabled} />
              <button type="button" className="btn btn-secondary" disabled={disabled || groupLinks.length <= 1} onClick={() => updateConfig({ group_links: groupLinks.filter((_, itemIndex) => itemIndex !== index) })}>x</button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary" style={{ justifySelf: 'start', padding: '0.35rem 0.8rem', fontSize: '0.75rem' }} disabled={disabled} onClick={() => updateConfig({ group_links: [...groupLinks, { name: '', url: '' }] })}>
            + Add Group Link
          </button>
        </div>
      </div>

      {['production', 'staging'].map(env => {
        const Section = allowCustomFields ? 'details' : 'div';
        const Heading = allowCustomFields ? 'summary' : 'div';
        return <Section key={env} style={sectionStyle} open={allowCustomFields ? env === 'production' : undefined}>
          <Heading style={{ marginBottom: '0.75rem', fontSize: '0.82rem', fontWeight: 900, color: 'var(--text-primary)', textTransform: 'uppercase' }}>
            {env === 'production' ? 'Production' : 'Staging'}
          </Heading>
          <div style={{ display: 'grid', gap: '0.85rem' }}>
            {[
              { key: 'bo_urls', label: 'Backoffice URLs', placeholder: 'BO URL' },
              { key: 'api_urls', label: 'API URLs', placeholder: 'API URL' }
            ].map(urlField => (
              <div key={`${env}-${urlField.key}`}>
                <label style={fieldLabelStyle}>{urlField.label}</label>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {normalizeUrlList(config.credentials?.[env]?.[urlField.key]).map((url, index) => (
                    <div key={`${env}-${urlField.key}-${index}`} style={{ display: 'flex', gap: '0.5rem' }}>
                      <input className="input" value={url || ''} onChange={(event) => updateUrl(env, urlField.key, index, event.target.value)} placeholder={`${env} ${urlField.placeholder}`} disabled={disabled} />
                      <button type="button" className="btn btn-secondary" disabled={disabled || normalizeUrlList(config.credentials?.[env]?.[urlField.key]).length <= 1} onClick={() => removeUrl(env, urlField.key, index)}>x</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary" style={{ justifySelf: 'start', padding: '0.35rem 0.8rem', fontSize: '0.75rem' }} disabled={disabled} onClick={() => addUrl(env, urlField.key)}>
                    + Add {urlField.placeholder}
                  </button>
                </div>
              </div>
            ))}

            {[{ title: 'BO Credentials', fields: boCredentialKeys }, { title: 'API Credentials', fields: apiCredentialKeys }].map(section => (
              section.fields.length > 0 && (
                <div key={`${env}-${section.title}`}>
                  <label style={fieldLabelStyle}>{section.title}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.65rem' }}>
                    {section.fields.map(field => (
                      <div key={`${env}-${field.key}`}>
                        <input
                          className="input"
                          value={config.credentials?.[env]?.[field.key] || ''}
                          onChange={(event) => updateCredentials(env, { [field.key]: event.target.value })}
                          placeholder={field.defaultValue ? `${field.key} default: ${field.defaultValue}` : field.key}
                          disabled={disabled}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}

            {allowCustomFields && <ProviderKeyValueFields label={credentialKeys.length ? "Additional credentials" : "Credentials"}
              value={Object.fromEntries(Object.entries(config.credentials?.[env] || {}).filter(([key]) => !reservedKeys.includes(key)))}
              reservedKeys={reservedKeys} disabled={disabled} onChange={value => onChange({ ...config, credentials: {
                ...config.credentials, [env]: { ...Object.fromEntries(Object.entries(config.credentials?.[env] || {}).filter(([key]) => reservedKeys.includes(key))), ...value }
              } })} />}

            {callbackDefs.length > 0 && (
              <div>
                <label style={fieldLabelStyle}>Callback Paths</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.65rem' }}>
                  {callbackDefs.map(callback => (
                    <input
                      key={`${env}-${callback.name}`}
                      className="input"
                      value={config.credentials?.[env]?.callback_paths?.[callback.name] || ''}
                      onChange={(event) => updateCredentials(env, {
                        callback_paths: {
                          ...(config.credentials?.[env]?.callback_paths || {}),
                          [callback.name]: event.target.value
                        }
                      })}
                      placeholder={`${callback.name || 'Callback'} path`}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </div>
            )}
            {allowCustomFields && <ProviderKeyValueFields label={callbackNames.length ? "Additional callback paths" : "Callback paths"}
              value={Object.fromEntries(Object.entries(config.credentials?.[env]?.callback_paths || {}).filter(([key]) => !callbackNames.includes(key)))}
              reservedKeys={callbackNames} disabled={disabled} onChange={value => updateCredentials(env, { callback_paths: {
                ...Object.fromEntries(Object.entries(config.credentials?.[env]?.callback_paths || {}).filter(([key]) => callbackNames.includes(key))), ...value
              } })} />}
          </div>
        </Section>;
      })}
    </div>
  );
};

export default ProviderChangeConfigFields;
